import { useState, useRef, useCallback } from "react";

export const useVideoRecorder = () => {
  const [stream, setStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  const [isCameraReady, setIsCameraReady] = useState(false);

  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null); // preview stream
  const recordingStreamRef = useRef(null); // recording stream
  const recordingPromiseRef = useRef(null);

  /* ---------------- CAMERA INIT ---------------- */
  const initializeCamera = useCallback(async () => {
    try {
      setError(null);
      setIsCameraReady(false);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, frameRate: 30 },
        audio: true,
      });

      streamRef.current = mediaStream;
      setStream(mediaStream);
      setIsCameraReady(true);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /* ---------------- START RECORDING ---------------- */
  const startRecording = useCallback(async () => {
    if (!streamRef.current) throw new Error("Camera not initialized");

    const rawStream = new MediaStream();
    streamRef.current.getTracks().forEach((track) => {
      rawStream.addTrack(track.clone());
    });

    recordingStreamRef.current = rawStream;
    chunksRef.current = [];

    const recorder = new MediaRecorder(rawStream, {
      mimeType: "video/webm",
      videoBitsPerSecond: 2500000,
    });

    recordingPromiseRef.current = new Promise((resolve, reject) => {
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        try {
          const blob = new Blob(chunksRef.current, { type: "video/webm" });
          const url = URL.createObjectURL(blob);
          resolve({
            blob,
            url,
            duration: recordingTime,
            fileSize: blob.size,
          });
        } catch (err) {
          reject(err);
        }
      };

      recorder.onerror = () => reject(new Error("Recording failed"));
    });

    recorder.start(1000);
    setMediaRecorder(recorder);
    setIsRecording(true);

    let time = recordingTime;
    timerRef.current = setInterval(() => {
      time += 1;
      setRecordingTime(time);
    }, 1000);
  }, [recordingTime]);

  /* ---------------- PAUSE ---------------- */
  const pauseRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.pause();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [mediaRecorder]);

  /* ---------------- RESUME ---------------- */
  const resumeRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state === "paused") {
      mediaRecorder.resume();
      setIsRecording(true);

      let time = recordingTime;
      timerRef.current = setInterval(() => {
        time += 1;
        setRecordingTime(time);
      }, 1000);
    }
  }, [mediaRecorder, recordingTime]);

  /* ---------------- STOP RECORDING ---------------- */
  const stopRecording = useCallback(async () => {
    if (!mediaRecorder) return null;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsRecording(false);

    if (mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }

    // 🔴 TURN CAMERA OFF
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((t) => t.stop());
      recordingStreamRef.current = null;
    }

    const video = await recordingPromiseRef.current;
    recordingPromiseRef.current = null;
    setRecordedVideo(video);
    return video;
  }, [mediaRecorder]);

  /* ---------------- STOP CAMERA ---------------- */
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((t) => t.stop());
      recordingStreamRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setStream(null);
    setIsCameraReady(false);
    setIsRecording(false);
  }, []);

  /* ---------------- RESET ---------------- */
  const resetRecording = useCallback(() => {
    if (recordedVideo) URL.revokeObjectURL(recordedVideo.url);
    setRecordedVideo(null);
    setRecordingTime(0);
    setError(null);
  }, [recordedVideo]);

  return {
    stream,
    recordedVideo,
    isRecording,
    recordingTime,
    error,
    isCameraReady,
    initializeCamera,
    startRecording,
    pauseRecording, // ✅ NOW EXISTS
    resumeRecording, // ✅ NOW EXISTS
    stopRecording,
    stopCamera,
    resetRecording,
  };
};
